import { memo } from 'react';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';

import { AnyObject } from '@typings';
import { PATH } from '@constants';
import {
  APP_INSTANCE_REF_LITERAL,
  CUSTOM_COMPONENT_NAME,
} from '@constants/shared.constants';
import { platformAppsInstancesSelector } from '@selectors';
import { useSelector } from '@hooks';
import {
  isArray,
  isBoolean,
  isNumber,
  isObject,
  isString,
  joinNonEmptyStrings,
  path,
} from '@utils';

import { Icon, Link, Render } from '@components/Shared';

type Props = {
  isSchemaArray?: boolean;
  isTopLevel?: boolean;
  schema: any;
  params: AnyObject;
  prefix?: string;
};

export const SchemaManagementParams: React.FC<Props> = memo(
  ({ isSchemaArray, isTopLevel, schema, params, prefix = '' }) => {
    const instances = useSelector(platformAppsInstancesSelector);

    const renderFormattedValue = (value: any) => {
      if (isBoolean(value)) {
        const formattedBooleanValue = value ? 'true' : 'false';

        return <span className="text-pink-800">{formattedBooleanValue}</span>;
      }

      if (value === null) {
        return <span className="text-neural-06">null</span>;
      }

      if (value === '') {
        return <span className="text-lime-800">&quot;&quot;</span>;
      }

      if (isString(value)) {
        return <span className="text-lime-800">{value}</span>;
      }

      if (isNumber(value)) {
        return <span className="text-cyan-800">{value}</span>;
      }

      return value;
    };

    const renderAppIntegration = (instanceId: string) => {
      const instance = instances.find(({ id }) => id === instanceId);
      const title = instance?.displayName || instance?.name || instanceId;

      return (
        <Link
          theme
          external
          blank
          to={path.appsInstance(instanceId)}
          className="flex items-center gap-1.5 text-primary"
        >
          {title} <Icon icon={faLink} className="text-[18px]" />
        </Link>
      );
    };

    const makeSchema = ([key, param]: [string, any]) => {
      const schemaKey = isSchemaArray ? '[]' : key;
      const fullKey = joinNonEmptyStrings(prefix, schemaKey);
      const metadata = params[fullKey];
      const isSecret =
        metadata?.type === CUSTOM_COMPONENT_NAME.SECRET[0] &&
        schemaKey === CUSTOM_COMPONENT_NAME.SECRET[1];
      const isAppIntegration =
        isObject(param) && param.type === APP_INSTANCE_REF_LITERAL;
      const isReference = isAppIntegration || isSecret;
      const isSchema = (isObject(param) || isArray(param)) && !isReference;

      return (
        <div key={joinNonEmptyStrings(prefix, key)} className="overflow-auto">
          <h4 className={clsx({ 'text-h6': isTopLevel })}>
            {metadata?.title ?? key}
            <Render if={isSchemaArray}> ({key})</Render>
          </h4>
          <Render if={isSchema}>
            <div className="flex flex-col gap-4 pl-4">
              <SchemaManagementParams
                isSchemaArray={isArray(param)}
                key={fullKey}
                prefix={fullKey}
                params={params}
                schema={param}
              />
            </div>
          </Render>
          <Render if={!isSchema && !isReference}>
            <p>{renderFormattedValue(param)}</p>
          </Render>
          <Render if={isAppIntegration}>
            {renderAppIntegration(param?.appInstanceId as string)}
          </Render>
          <Render if={isSecret}>
            <Link
              theme
              to={PATH.SECRETS}
              className="flex items-center gap-1.5 text-primary"
            >
              {param} <Icon icon={faLink} className="text-[18px]" />
            </Link>
          </Render>
        </div>
      );
    };

    return <>{Object.entries(schema).map(makeSchema)}</>;
  },
);
